import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Open/closed and width for the docked AI CIO pane.
 *
 * Lifted out of the pane itself so the layout can make room for it. The pane is fixed to
 * the right edge, so left to itself it covers the page — and the whole point of docking
 * the agent rather than opening its own page is to ask about what you are looking at.
 * Covering it would be worse than the full page, not better.
 */

const OPEN_KEY = 'plutus.cio.dock.open'
const WIDTH_KEY = 'plutus.cio.dock.width'

export const DOCK_MIN_W = 340
export const DOCK_MAX_W = 760
export const DOCK_DEFAULT_W = 430

export function useCioDock() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(OPEN_KEY) === '1' } catch { return false }
  })
  const [width, setWidth] = useState(() => {
    try {
      const v = Number(localStorage.getItem(WIDTH_KEY))
      return Number.isFinite(v) && v >= DOCK_MIN_W && v <= DOCK_MAX_W ? v : DOCK_DEFAULT_W
    } catch { return DOCK_DEFAULT_W }
  })
  const dragging = useRef(false)

  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, open ? '1' : '0') } catch { /* private window */ }
  }, [open])

  // Listeners on window rather than the handle, so the pointer can outrun the drag
  // without the pane detaching from the cursor.
  const startDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragging.current = true
    const move = (ev: MouseEvent) => {
      if (!dragging.current) return
      setWidth(Math.min(DOCK_MAX_W, Math.max(DOCK_MIN_W, window.innerWidth - ev.clientX)))
    }
    const up = () => {
      dragging.current = false
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      document.body.style.userSelect = ''
      setWidth(w => {
        try { localStorage.setItem(WIDTH_KEY, String(w)) } catch { /* ignore */ }
        return w
      })
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }, [])

  return { open, setOpen, width, startDrag }
}
